import { useState, useRef, useEffect, useCallback } from 'react';
import SlickSlider from 'react-slick';
import styled, { css } from 'styled-components';
import 'slick-carousel/slick/slick.css';
import { container, spacing } from 'components/atoms/Grid/constants';
import Colors from 'components/tokens/Colors';
import { BreakPoint } from 'components/atoms/Grid';
import Icon from 'components/atoms/Icon';

function Slider({ handleFogEffect, handleClickedSlider, overflow, ...props }) {
    const slickRef = useRef();
    const [currentSlide, setCurrentSlide] = useState(0);

    const setClickedSlider = useCallback(
        clickedSlider => {
            handleClickedSlider && handleClickedSlider(clickedSlider);
        },
        [handleClickedSlider]
    );

    const nextSlide = () => {
        slickRef.current.slickNext();
    };

    const prevSlide = () => {
        slickRef.current.slickPrev();
    };

    useEffect(() => {
        if (handleFogEffect) {
            handleFogEffect(currentSlide, slickRef?.current?.props?.children?.length);
        }

        setClickedSlider(currentSlide);
    }, [currentSlide, handleFogEffect, setClickedSlider]);

    const CustomArrow = props => {
        return (
            <ArrowItem
                next={props.next}
                onClick={props.next ? nextSlide : prevSlide}
                education={props.education}
                disabled={
                    props.newsSlider
                        ? null
                        : props.next
                        ? currentSlide === slickRef?.current?.props?.children?.length - 1
                        : currentSlide === 0
                }
            >
                {props.next ? (
                    <Icon name="ArrowRight" color={props.arrowsColor ?? Colors.primary} />
                ) : (
                    <Icon name="ArrowLeft" color={props.arrowsColor ?? Colors.primary} />
                )}
            </ArrowItem>
        );
    };

    const settings = {
        dots: props.dots ?? true,
        infinite: props.infinite ?? true,
        speed: 500,
        slidesToShow: props.slidesToShow ?? 1,
        slidesToScroll: props.slidesToScroll ?? 1,
        autoplay: props.autoplay ?? true,
        autoplaySpeed: 5000,
        arrows: props.arrows,
        nextArrow: <CustomArrow next {...props} />,
        prevArrow: <CustomArrow {...props} />,
        lazyLoad: true,
        beforeChange: (prev, next) => {
            setCurrentSlide(next);
        },
        appendDots: dots => {
            return (
                <ul className="slick-dots">
                    {dots.map((item, index) => {
                        return <li key={index}>{item.props.children}</li>;
                    })}
                </ul>
            );
        },
        customPaging: index => {
            return <SlideDotItem dark={props.darkDots} className={index === currentSlide ? '_active' : null} />;
        }
    };

    return (
        <SlickContainer className="slider-c" overflow={overflow} {...props}>
            <SlickSlider ref={slickRef} {...settings}>
                {props.children}
            </SlickSlider>
        </SlickContainer>
    );
}

const ArrowItem = styled.div`
    opacity: ${({ disabled, education }) => (disabled && education ? 0.5 : disabled ? 0 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
    position: absolute;
    top: 50%;
    z-index: 2;
    cursor: pointer;
    left: ${({ next }) => (next ? '' : '32px')};
    transform: translate(0, -50%) scale(0.6);

    ${props =>
        props.next &&
        css`
            right: 32px;
        `}

    @media(min-width: 768px) {
        transform: translate(0, -50%) scale(1);
        left: ${({ next }) => (next ? '' : '64px')};

        ${props =>
            props.next &&
            css`
                right: 64px;
            `}
    }
`;

const SlideDotItem = styled.div`
    border: 2px solid ${Colors.white};
    background-color: ${Colors.white};
    margin-right: 24px;
    width: 48px;
    cursor: pointer;
    opacity: 0.5;

    &._active {
        border-color: ${Colors.white};
        background-color: ${Colors.white};
        opacity: 1;
    }

    ${props =>
        props.dark &&
        css`
            border: 2px solid ${Colors.black};
            background-color: ${Colors.black};
            opacity: 1;

            &._active {
                border-color: ${Colors.primary};
                background-color: ${Colors.primary};
            }
        `}
`;

const SlickContainer = styled.div`
    overflow: ${({ overflow }) => overflow ?? 'hidden'};
    float: left;
    width: 100%;
    height: 100%;

    ${BreakPoint(
        'sm',
        css`
            .slick-list {
                height: ${props => props.height ?? 700}px;
            }
        `
    )}

    .slick-dots {
        padding: 0;
        margin: 0 auto;
        width: ${container}px;
        position: relative;
        padding-left: ${spacing * 3}px;
        padding-right: ${spacing * 2}px;
        position: relative;
        top: -60px;
        margin-bottom: -4px;
        display: flex;
        justify-content: flex-start;

        @media (min-width: 768px) {
            padding-left: ${spacing * 8}px;
        }

        ${props =>
            props.right &&
            css`
                ${BreakPoint(
                    'sm',
                    css`
                        justify-content: flex-end;
                    `
                )}
            `}

        ${props =>
            props.center &&
            css`
                ${BreakPoint(
                    'sm',
                    css`
                        justify-content: center;
                    `
                )}
            `}

		> li {
            list-style: none;
            padding: 0;
            margin: 0;

            &:last-child ${SlideDotItem} {
                margin-right: 0;
            }
        }
    }
`;

export const SlideItem = styled.div`
    float: left;
    width: 100%;
    overflow: ${({ overflow }) => overflow ?? 'hidden'};

    ${BreakPoint(
        'sm',
        css`
            height: ${props => props.height ?? 700}px;
        `
    )}

    @media (max-width: 820px) {
        height: ${props => props.mobileHeight}px;
    }

    ${props => css`
        background-color: ${props.bg};
    `}

    ${props =>
        props.bgImg &&
        css`
            background-size: cover;
            background-position: center;
            background-image: url('${props.bgImg}');
            height: 574px;
        `}
`;

export default Slider;
